"use client";

import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Mic,
  MicOff,
  Keyboard,
  Square,
  Trash2,
  Paperclip,
  Image,
  FileText,
  X,
  Loader2,
} from "lucide-react";
import { useSettings } from "@/lib/settings-context";
import { useLanguage } from "@/lib/language-context";
import { useTranslations } from "@/lib/use-translations";
import { useSpeechToText } from "@/lib/use-speech-to-text";
type DialogueRichness = Richness;
import { cn } from "@/lib/utils";
import { useAnalytics } from "@/lib/use-analytics";
import type { GenerationParams } from "@/hooks/use-generation-service";
import type { LocaleCode, Difficulty, Richness } from "@/lib/api/client";

// Data for editing/regenerating a topic
export interface InitialTopicData {
  topic: string;
  difficulty?: Difficulty;
  richness?: Richness;
  autoGenerate?: boolean; // true for regenerate (skip wizard), false for edit (show wizard)
}

// Base info for building a live topic during streaming
export interface StreamingTopicBase {
  id: string;
  title: string;
  originalPrompt: string;
  language: LocaleCode;
  difficulty: Difficulty;
  richness: Richness;
}

interface CustomTopicGeneratorProps {
  onBack: () => void;
  onStartGeneration: (params: GenerationParams, topicBase: StreamingTopicBase) => void;
  onOpenSettings?: () => void;
  initialTopic?: InitialTopicData | null;
}

// Typeform-style step
type FormStep = "topic" | "difficulty" | "richness";

export function CustomTopicGenerator({ onBack, onStartGeneration, initialTopic }: CustomTopicGeneratorProps) {
  const { userProfile, analyticsOptOut, tts } = useSettings();
  const { nativeLanguage, targetLanguage } = useLanguage();
  const t = useTranslations();
  const { track, events } = useAnalytics();

  // Get default speaking style from user profile
  const defaultSoundStyle = userProfile.speakingStyle || "beginner";
  const defaultCustomStyles = userProfile.customStyles || [];

  // Form state - initialize from props if editing/regenerating
  const [currentStep, setCurrentStep] = useState<FormStep>("topic");
  const [topicInput, setTopicInput] = useState(initialTopic?.topic || "");
  const [interimText, setInterimText] = useState("");
  const [inputMode, setInputMode] = useState<"mic" | "keyboard">(initialTopic ? "keyboard" : "mic");
  const [difficulty, setDifficulty] = useState<Difficulty>(
    initialTopic?.difficulty || "beginner"
  );
  const [soundStyle, setSoundStyle] = useState<"beginner" | "local" | "other">(defaultSoundStyle as "beginner" | "local" | "other");
  const [_customSoundStyles, setCustomSoundStyles] = useState<string[]>(defaultCustomStyles);
  const [_customSoundStyleInput, setCustomSoundStyleInput] = useState("");
  const [richness, setRichness] = useState<DialogueRichness>(
    initialTopic?.richness || "standard"
  );
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // For regeneration: track if we should auto-start (only when autoGenerate is true)
  const shouldAutoGenerateRef = useRef(!!initialTopic?.autoGenerate);

  // Speech-to-text
  const handleSpeechResult = useCallback((transcript: string) => {
    setTopicInput(prev => prev ? `${prev} ${transcript}` : transcript);
    setInterimText("");
  }, []);
  const handleInterimResult = useCallback((transcript: string) => {
    setInterimText(transcript);
  }, []);
  const {
    isRecording,
    isProcessing,
    error: speechError,
    toggleRecording,
  } = useSpeechToText({
    language: nativeLanguage.code || "en-US",
    onResult: handleSpeechResult,
    onInterim: handleInterimResult,
  });



  // Check if current request is cached
  const cacheRequest = useMemo(() => ({
    topic: topicInput.trim(),
    nativeLanguage: nativeLanguage.code,
    targetLanguage: targetLanguage.code,
    difficulty,
    richness,
  }), [topicInput, nativeLanguage.code, targetLanguage.code, difficulty, richness]);

  // Navigation functions
  const steps: FormStep[] = ["topic", "difficulty", "richness"];
  const currentStepIndex = steps.indexOf(currentStep);

  // Track if we should start generating after richness step
  const shouldStartGeneratingRef = useRef(false);

  const triggerGeneration = useCallback((skipCache = false) => {
    if (!topicInput.trim()) return;

    const params: GenerationParams = {
      topic: topicInput.trim(),
      nativeLanguage: nativeLanguage.code,
      targetLanguage: targetLanguage.code,
      targetLocale: targetLanguage.code,
      difficulty,
      richness,
      userName: userProfile.name || undefined,
      userGender: userProfile.gender || undefined,
      knownLanguages: userProfile.knownLanguages || undefined,
      analyticsOptOut: analyticsOptOut || undefined,
      ttsSpeed: tts.speed,
      skipCache,
      cacheRequest,
    };

    const topicBase: StreamingTopicBase = {
      id: `gen-${Date.now()}`,
      title: topicInput.trim(),
      originalPrompt: topicInput.trim(),
      language: targetLanguage.code,
      difficulty,
      richness,
    };

    track(events.CUSTOM_TOPIC_CREATED, { difficulty, richness });
    onStartGeneration(params, topicBase);
  }, [topicInput, nativeLanguage.code, targetLanguage.code,
      difficulty, richness, userProfile, analyticsOptOut, tts.speed,
      cacheRequest, track, events, onStartGeneration]);

  const goToNextStep = useCallback(() => {
    if (currentStep === "topic" && !topicInput.trim()) return;
    const nextIndex = currentStepIndex + 1;
    const nextStep = steps[nextIndex];
    if (nextIndex < steps.length && nextStep) {
      setCurrentStep(nextStep);
    } else if (currentStep === "richness") {
      // After richness, start generating (switch to learning view)
      shouldStartGeneratingRef.current = true;
      triggerGeneration(false);
    }
  }, [currentStep, currentStepIndex, topicInput, steps, triggerGeneration]);

  const goToPrevStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    const prevStep = steps[prevIndex];
    if (prevIndex >= 0 && prevStep) {
      setCurrentStep(prevStep);
    } else {
      onBack();
    }
  }, [currentStepIndex, steps, onBack]);

  // Auto-trigger generation when initialTopic is provided (for regeneration)
  useEffect(() => {
    if (shouldAutoGenerateRef.current) {
      shouldAutoGenerateRef.current = false;
      triggerGeneration(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sample scenarios for inspiration - from translations
  const sampleScenarios = t.generator.sampleScenarios;

  // Example card state with swipe support
  const [exampleIndex, setExampleIndex] = useState(() => Math.floor(Math.random() * sampleScenarios.length));
  const [swipeDirection, setSwipeDirection] = useState<"left" | "right" | null>(null);
  const [isAutoSwiping, setIsAutoSwiping] = useState(true);
  const touchStartX = useRef<number>(0);

  const nextExample = useCallback(() => {
    setSwipeDirection("left");
    setTimeout(() => {
      setExampleIndex((prev) => (prev + 1) % sampleScenarios.length);
      setSwipeDirection(null);
    }, 150);
  }, [sampleScenarios.length]);

  const prevExample = useCallback(() => {
    setSwipeDirection("right");
    setTimeout(() => {
      setExampleIndex((prev) => (prev - 1 + sampleScenarios.length) % sampleScenarios.length);
      setSwipeDirection(null);
    }, 150);
  }, [sampleScenarios.length]);

  // Auto-swipe effect
  useEffect(() => {
    if (!isAutoSwiping || topicInput || currentStep !== "topic") return;

    const interval = setInterval(() => {
      nextExample();
    }, 4000); // Swipe every 4 seconds

    return () => clearInterval(interval);
  }, [isAutoSwiping, topicInput, currentStep, nextExample]);

  // Stop auto-swipe on user interaction
  const handleManualSwipe = useCallback((direction: "next" | "prev") => {
    setIsAutoSwiping(false);
    if (direction === "next") {
      nextExample();
    } else {
      prevExample();
    }
  }, [nextExample, prevExample]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartX.current = touch.clientX;
    setIsAutoSwiping(false); // Stop auto-swipe on touch
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const changedTouch = e.changedTouches[0];
    if (!changedTouch) return;
    const touchEndX = changedTouch.clientX;
    const diff = touchStartX.current - touchEndX;

    if (Math.abs(diff) > 50) { // Minimum swipe distance
      if (diff > 0) {
        nextExample();
      } else {
        prevExample();
      }
    }
  }, [nextExample, prevExample]);

  // Step swipe handlers (separate from example card swipe)
  const stepTouchStartX = useRef<number>(0);
  const stepTouchStartY = useRef<number>(0);

  const handleStepTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    stepTouchStartX.current = touch.clientX;
    stepTouchStartY.current = touch.clientY;
  }, []);

  const handleStepTouchEnd = useCallback((e: React.TouchEvent) => {
    const changedTouch = e.changedTouches[0];
    if (!changedTouch) return;
    const touchEndX = changedTouch.clientX;
    const touchEndY = changedTouch.clientY;
    const diffX = stepTouchStartX.current - touchEndX;
    const diffY = stepTouchStartY.current - touchEndY;

    // Only trigger if horizontal swipe is dominant (not scrolling vertically)
    if (Math.abs(diffX) > 80 && Math.abs(diffX) > Math.abs(diffY) * 1.5) {
      if (diffX < 0) {
        // Swipe right → go back
        goToPrevStep();
      }
      // Swipe left doesn't auto-advance (user must explicitly choose options)
    }
  }, [goToPrevStep]);

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case "topic":
        return (
          <div className="flex flex-col items-center justify-center min-h-[50vh] px-6">
            <div className="w-full max-w-lg space-y-6">
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold text-foreground">
                  {t.generator.topicTitle}
                </h2>
                <p className="text-muted-foreground">
                  {inputMode === "mic" ? t.generator.topicHintMic : t.generator.topicHintKeyboard}
                </p>
              </div>

              {/* Example scenario - swipeable card */}
              {!topicInput && !interimText && (
                <div className="relative">
                  {/* Navigation arrows */}
                  <button
                    type="button"
                    onClick={() => handleManualSwipe("prev")}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-8 h-8 rounded-full bg-background border border-border shadow-sm flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleManualSwipe("next")}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-8 h-8 rounded-full bg-background border border-border shadow-sm flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  {/* Card */}
                  <button
                    type="button"
                    onClick={() => {
                      const scenario = sampleScenarios[exampleIndex];
                      if (scenario) setTopicInput(scenario);
                      setInputMode("keyboard");
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    className={cn(
                      "w-full p-4 bg-muted/30 hover:bg-muted/50 border border-dashed border-muted-foreground/30 rounded-xl transition-all text-left",
                      swipeDirection === "left" && "translate-x-[-10px] opacity-50",
                      swipeDirection === "right" && "translate-x-[10px] opacity-50"
                    )}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-muted-foreground">{t.generator.exampleTapToUse}</p>
                      <p className="text-xs text-muted-foreground">{exampleIndex + 1}/{sampleScenarios.length}</p>
                    </div>
                    <p className="text-foreground italic text-sm leading-relaxed">&ldquo;{sampleScenarios[exampleIndex]}&rdquo;</p>
                  </button>

                  {/* Dots indicator */}
                  <div className="flex justify-center gap-1.5 mt-3">
                    {sampleScenarios.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => {
                          setIsAutoSwiping(false);
                          setExampleIndex(i);
                        }}
                        className={cn(
                          "w-1.5 h-1.5 rounded-full transition-colors",
                          i === exampleIndex ? "bg-primary" : "bg-muted-foreground/30"
                        )}
                      />
                    ))}
                  </div>
                </div>
              )}

              {inputMode === "mic" ? (
                <>
                  {/* Three-button row: Enter text | Tap and talk | Attach */}
                  <div className="flex items-center justify-center gap-6">
                    {/* Enter text button */}
                    <button
                      type="button"
                      onClick={() => setInputMode("keyboard")}
                      className="flex flex-col items-center gap-2 group"
                    >
                      <div className="w-14 h-14 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors group-hover:scale-105">
                        <Keyboard className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <span className="text-xs text-muted-foreground">{t.generator.enterText}</span>
                    </button>

                    {/* Main mic button */}
                    <div className="flex flex-col items-center gap-2">
                      <button
                        type="button"
                        onClick={toggleRecording}
                        disabled={isProcessing}
                        className={cn(
                          "w-24 h-24 rounded-full flex items-center justify-center transition-all shadow-lg",
                          isRecording
                            ? "bg-destructive text-destructive-foreground scale-110"
                            : "bg-primary text-primary-foreground hover:scale-105",
                          isProcessing && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        {isProcessing ? (
                          <Loader2 className="w-10 h-10 animate-spin" />
                        ) : isRecording ? (
                          <Square className="w-10 h-10 fill-current" />
                        ) : (
                          <Mic className="w-10 h-10" />
                        )}
                      </button>
                      <span className="text-xs text-muted-foreground">
                        {isRecording ? t.generator.tapToStop : isProcessing ? t.generator.processing : t.generator.tapAndTalk}
                      </span>
                    </div>

                    {/* Attach button */}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex flex-col items-center gap-2 group"
                    >
                      <div className="w-14 h-14 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors group-hover:scale-105">
                        <Paperclip className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <span className="text-xs text-muted-foreground">{t.generator.attach}</span>
                    </button>
                  </div>

                  {/* Recording indicator */}
                  {isRecording && (
                    <div className="flex items-center justify-center gap-2 text-destructive animate-pulse">
                      <div className="w-2 h-2 rounded-full bg-destructive animate-ping" />
                      <span className="text-sm font-medium">{t.generator.recording}</span>
                    </div>
                  )}

                  {/* Transcribed text display */}
                  {(topicInput || interimText) && (
                    <div className="relative p-4 bg-muted/50 rounded-xl border border-border min-h-[80px]">
                      {/* Clear button */}
                      <button
                        type="button"
                        onClick={() => {
                          setTopicInput("");
                          setInterimText("");
                        }}
                        className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-muted hover:bg-destructive/20 hover:text-destructive transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      <p className="text-lg text-foreground pr-6">
                        {topicInput}
                        {interimText && (
                          <span className="text-muted-foreground italic"> {interimText}</span>
                        )}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Keyboard input mode */}
                  <Textarea
                    autoFocus
                    placeholder={t.generator.placeholder}
                    value={topicInput}
                    onChange={(e) => setTopicInput(e.target.value)}
                    className="min-h-[120px] text-lg resize-none border-2 focus:border-primary"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && topicInput.trim()) {
                        e.preventDefault();
                        goToNextStep();
                      }
                    }}
                  />

                  {/* Toggle buttons row */}
                  <div className="flex items-center justify-center gap-4">
                    <button
                      type="button"
                      onClick={() => setInputMode("mic")}
                      className="flex flex-col items-center gap-1.5 group"
                    >
                      <div className="w-12 h-12 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-colors group-hover:scale-105">
                        <Mic className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-xs text-muted-foreground">{t.generator.useVoice}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex flex-col items-center gap-1.5 group"
                    >
                      <div className="w-12 h-12 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors group-hover:scale-105">
                        <Paperclip className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <span className="text-xs text-muted-foreground">{t.generator.attach}</span>
                    </button>
                  </div>
                </>
              )}

              {/* Hidden file inputs */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt,image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length) {
                    setAttachments(prev => [...prev, ...files]);
                  }
                  e.target.value = "";
                }}
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length) {
                    setAttachments(prev => [...prev, ...files]);
                  }
                  e.target.value = "";
                }}
              />

              {/* Attached files display */}
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {attachments.map((file, index) => {
                    const isImage = file.type.startsWith("image/");
                    return (
                      <div
                        key={`${file.name}-${index}`}
                        className="relative group flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg border border-border"
                      >
                        {isImage ? (
                          <Image className="w-4 h-4 text-primary" />
                        ) : (
                          <FileText className="w-4 h-4 text-primary" />
                        )}
                        <span className="text-sm truncate max-w-[120px]">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => setAttachments(prev => prev.filter((_, i) => i !== index))}
                          className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-destructive/20 hover:text-destructive transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Speech error */}
              {speechError && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 rounded-lg text-sm text-destructive">
                  <MicOff className="w-4 h-4" />
                  {speechError}
                </div>
              )}

              {/* Continue button */}
              <Button
                onClick={goToNextStep}
                disabled={!topicInput.trim()}
                className="w-full h-14 text-lg gap-2"
              >
                {t.generator.continue}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        );

      case "difficulty":
        return (
          <div className="flex flex-col items-center justify-center min-h-[50vh] px-6">
            <div className="w-full max-w-lg space-y-6">
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold text-foreground">
                  {t.generator.difficultyTitle}
                </h2>
                <p className="text-muted-foreground">
                  {t.generator.difficultySubtitle}
                </p>
              </div>

              <div className="space-y-3">
                {/* Beginner option */}
                <button
                  type="button"
                  onClick={() => {
                    setSoundStyle("beginner");
                    setDifficulty("beginner");
                    setCustomSoundStyles([]);
                    setCustomSoundStyleInput("");
                    goToNextStep();
                  }}
                  className={cn(
                    "w-full p-5 text-left rounded-xl border-2 transition-all",
                    soundStyle === "beginner"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50 hover:bg-muted/50"
                  )}
                >
                  <div className="font-semibold text-lg text-foreground">{t.generator.styleBeginner}</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {t.generator.styleBeginnerDesc}
                  </div>
                </button>

                {/* Local option */}
                <button
                  type="button"
                  onClick={() => {
                    setSoundStyle("local");
                    setDifficulty("advanced");
                    setCustomSoundStyles([]);
                    setCustomSoundStyleInput("");
                    goToNextStep();
                  }}
                  className={cn(
                    "w-full p-5 text-left rounded-xl border-2 transition-all",
                    soundStyle === "local"
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50 hover:bg-muted/50"
                  )}
                >
                  <div className="font-semibold text-lg text-foreground">{t.generator.styleLocal}</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {t.generator.styleLocalDesc}
                  </div>
                </button>

              </div>
            </div>
          </div>
        );

      case "richness":
        return (
          <div className="flex flex-col items-center justify-center min-h-[50vh] px-6">
            <div className="w-full max-w-lg space-y-6">
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold text-foreground">
                  {t.generator.richnessTitle}
                </h2>
                <p className="text-muted-foreground">
                  {t.generator.richnessSubtitle}
                </p>
              </div>

              <div className="space-y-3">
                {([
                  { value: "compact" as const, label: t.generator.richnessQuick, desc: t.generator.richnessQuickDesc },
                  { value: "standard" as const, label: t.generator.richnessStandard, desc: t.generator.richnessStandardDesc },
                  { value: "comprehensive" as const, label: t.generator.richnessComprehensive, desc: t.generator.richnessComprehensiveDesc },
                ]).map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setRichness(option.value)}
                    className={cn(
                      "w-full p-5 text-left rounded-xl border-2 transition-all",
                      richness === option.value
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50 hover:bg-muted/50"
                    )}
                  >
                    <div className="font-semibold text-lg text-foreground">
                      {option.label}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {option.desc}
                    </div>
                  </button>
                ))}
              </div>

              <div className="flex justify-center pt-2">
                <Button onClick={goToNextStep} size="lg">
                  {t.generator.continue}
                </Button>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col h-[100dvh] bg-background">
      {/* Fixed navbar */}
      <div className="shrink-0 px-6 pt-6 pb-2">
          <div className="w-full max-w-lg mx-auto flex items-center">
            <button
              type="button"
              onClick={goToPrevStep}
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">{t.generator.back}</span>
            </button>
            <div className="flex-1 flex justify-center items-center gap-2">
              {steps.map((step, i) => (
                <div
                  key={step}
                  className={cn(
                    "rounded-full transition-all duration-300 ease-out",
                    i === currentStepIndex
                      ? "w-2.5 h-2.5 bg-primary scale-100"
                      : i < currentStepIndex
                        ? "w-2 h-2 bg-primary/60"
                        : "w-2 h-2 bg-muted-foreground/30"
                  )}
                />
              ))}
            </div>
            <div className="w-12" />
          </div>
        </div>

      {/* Content - swipeable to navigate back */}
      <div
        className="flex-1 overflow-y-auto min-h-0 pt-[3vh]"
        onTouchStart={handleStepTouchStart}
        onTouchEnd={handleStepTouchEnd}
      >
        {renderStepContent()}
      </div>
    </div>
  );
}
